import { useMemo } from "react";
import { useNavigate } from "react-router";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useExpenses } from "../../../lib/expenses/useExpenses";
import { useVendors } from "../../../lib/vendors/useVendors";
import {
  Card,
  EmptyState,
  formatCents,
  withinRange,
  computeRange,
  type RangePreset,
} from "./shared";

const BAR_COLOR = "#0070F2";

export function TopVendors({ preset }: { preset: RangePreset }) {
  const { expenses } = useExpenses();
  const { vendors } = useVendors();
  const navigate = useNavigate();

  const data = useMemo(() => {
    const { startIso, endIso } = computeRange(preset);
    const byId = new Map<string, number>();
    const byName = new Map<string, number>();
    for (const e of expenses) {
      if (!withinRange(e.occurredOn, startIso, endIso)) continue;
      if (e.vendorId) {
        byId.set(e.vendorId, (byId.get(e.vendorId) ?? 0) + e.amountCents);
      } else {
        const key = e.vendor?.trim() || "Unknown";
        byName.set(key, (byName.get(key) ?? 0) + e.amountCents);
      }
    }
    const vendorIndex = new Map(vendors.map((v) => [v.id, v]));
    const rows: Array<{ id: string | null; name: string; value: number }> = [];
    for (const [id, value] of byId.entries()) {
      const v = vendorIndex.get(id);
      rows.push({ id, name: v?.canonicalName ?? "Unknown", value });
    }
    for (const [name, value] of byName.entries()) {
      rows.push({ id: null, name, value });
    }
    return rows.sort((a, b) => b.value - a.value).slice(0, 10);
  }, [expenses, vendors, preset]);

  return (
    <Card title="Top vendors" testId="report-top-vendors">
      {data.length === 0 ? (
        <EmptyState message="No vendor activity in this window yet." />
      ) : (
        <div style={{ height: Math.max(240, data.length * 28 + 40) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
            >
              <XAxis
                type="number"
                tickFormatter={(v) => formatCents(Number(v))}
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={120}
                tick={{ fontSize: 11, fill: "#334155" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(v: number) => formatCents(v)}
                cursor={{ fill: "rgba(0,112,242,0.05)" }}
              />
              <Bar
                dataKey="value"
                isAnimationActive={false}
                radius={[0, 4, 4, 0]}
              >
                {data.map((row, i) => (
                  <Cell
                    key={i}
                    fill={BAR_COLOR}
                    cursor={row.id ? "pointer" : "default"}
                    onClick={() => {
                      if (row.id) navigate(`/vendors/${row.id}`);
                    }}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
